/**
 * Default English strings for the MFA verification component.
 *
 * Apps can override any string by passing a `t(key, params)` function
 * prop to MfaVerification. The keys below are the lookup keys. Strings
 * with `{{param}}` placeholders are interpolated at render time.
 *
 * @module @puffin-core/mfa-ui/strings/en
 */

// 🔴 The React-FREE `/strings-kit` subpath, never the package barrel. This file is part of the
// leaf-only `strings/` subtree that `@puffin-core/i18n-namespaces` imports at app boot; a barrel
// import would pull `createAppI18n` — and through it React — into ten apps' boot chunks.
import { createInterpolator } from '@puffin-core/i18n-bootstrap/strings-kit';

// Frozen, matching every sibling shared bundle (`auth-shell`, `login-shell`, `page-shell`,
// `captcha-ui`, `re-auth`). It is registered onto a live i18next instance and handed to every
// consumer, so an accidental mutation would travel to all of them.
export const DEFAULT_MFA_STRINGS = Object.freeze({
  title: 'Verify Your Identity',
  subtitle_totp: 'Enter the 6-digit code from your authenticator app.',
  subtitle_sms: 'Enter the code sent to {{maskedPhone}}.',
  subtitle_email: 'Enter the code sent to {{maskedEmail}}.',
  subtitle_send_sms: "We'll send a code to {{maskedPhone}}.",
  subtitle_send_email: "We'll send a code to {{maskedEmail}}.",
  // ECOSYSTEM-EMAIL-001 Q-EE-34 P5: a passkey answers the second factor with the device's screen lock.
  subtitle_passkey: 'Use the passkey saved on this device.',
  label_code: 'Verification Code',
  label_backup: 'Backup Code',
  btn_verify: 'Verify',
  btn_use_passkey: 'Use passkey',
  btn_verifying: 'Verifying...',
  btn_back: 'Back',
  btn_send: 'Send Code',
  btn_sending: 'Sending...',
  btn_resend: 'Resend code',
  resend_countdown: '({{seconds}}s)',
  link_try_another: 'Try another way',
  link_backup: 'Use a backup code',
  // MFA-LOGIN-CONSOLIDATION-001: shown on a TERMINAL verify error (challenge
  // invalid / locked / session expired) — the user must restart from sign-in.
  link_return_to_signin: 'Return to sign-in',
  title_backup: 'Use a Backup Code',
  subtitle_backup: 'Enter one of your 8-character backup codes.',
  title_methods: 'Choose a verification method.',
  method_totp: 'Authenticator App',
  method_totp_hint: 'Enter code from your app',
  method_sms: 'Text Message',
  method_email: 'Email',
  method_passkey: 'Passkey',
  method_passkey_hint: "Use your device's screen lock",

  // F-PASSKEY-HOST (2026-10-24; owner ruling Q-EE-49): no method the challenge names can be answered here — a passkey
  // registered at another app's address, a method this screen cannot run. The way out follows: the backup-code entry,
  // (when no backup code remains either, the challenge refuses with `error_no_usable_method`).
  notice_no_usable_method: 'None of your verification methods can be used here.',

  // 🔴 Added 2026-08-19 by a trust-but-verify audit. THREE mfa-ui components rendered hardcoded
  // ENGLISH in every locale — `MfaStatusCard`, `BackupCodesCard` and `MfaSetupWizard` used the
  // translate hook NOWHERE. The AAS-I18N-001 close-out gave this package an `es.js`, a hook and a
  // namespace, and wired `MfaVerification`; it did not touch the other three, and nothing reported
  // them: `web/no-hardcoded-strings` scopes to `apps/**/src`, so a literal in a shared package is
  // invisible to it forever (miss-class 9).
  status_title: 'Multi-Factor Authentication',
  status_enabled: 'Enabled',
  status_disabled: 'Disabled',
  status_desc_enabled:
    'MFA is enabled using {{methods}}. You will be prompted for a verification code when logging in.',
  status_desc_disabled:
    'Add an extra layer of security to your account by enabling multi-factor authentication.',
  status_btn_enable: 'Enable MFA',
  backup_btn_regenerate: 'Regenerate Backup Codes',
  backup_btn_regenerating: 'Regenerating...',
  setup_btn_verify: 'Verify',
  setup_btn_verifying: 'Verifying...',
  backup_title: 'Backup Codes',
  setup_title_configure: 'Set Up Authenticator',
  setup_manual_key: 'Manual Entry Key',
  setup_title_verify: 'Verify Setup',
  setup_title_backup: 'Save Backup Codes',
  setup_region_label: 'MFA Setup',
  setup_step_configure: 'Configure',
  setup_step_verify: 'Verify',
  setup_step_backup: 'Backup Codes',
  setup_configure_intro:
    'Scan the QR code with your authenticator app to add {{brand}}, or enter the key below by hand.',
  setup_qr_label: 'QR code that adds {{brand}} to an authenticator app',
  setup_uri_label: 'Setup link (opens your authenticator app on this device)',
  setup_qr_loading: 'Drawing the QR code…',
  setup_qr_unavailable:
    'The QR code could not be drawn. Enter the key below by hand, or open the setup link.',
  setup_btn_cancel: 'Cancel',
  setup_btn_next: 'Next',
  enroll_title: 'Set up two-step verification',
  enroll_intro:
    'Your {{appName}} account needs a second sign-in step before you can continue. Confirm your password to begin.',
  enroll_password_label: 'Password',
  enroll_btn_continue: 'Continue',
  enroll_btn_cancel: 'Cancel',
  enroll_already_title: 'Two-step verification is already set up',
  enroll_already_body:
    'Two-step verification is already set up for this account. Sign in to continue.',
  enroll_already_backup_codes:
    'If you did not save your backup codes, sign in and use Regenerate Backup Codes in your security settings. Regenerating replaces any codes from this setup.',
  enroll_already_btn_sign_in: 'Sign in',
  // SECREQ S6b: a sign-in held for a passkey, in an app that sets one up (`<PasskeyEnrollment>`).
  enroll_passkey_title: 'Set up a passkey',
  enroll_passkey_intro:
    "Your company requires a passkey for your {{appName}} account. Use your device's screen lock to create one, then continue signing in.",
  enroll_passkey_btn_create: 'Create passkey',
  enroll_passkey_already_title: 'A passkey is already set up',
  enroll_passkey_already_body: 'This account already has a passkey. Sign in with it to continue.',
  // N152 (MFA-SETUP-CONVERGE): the wizard's verify and backup steps and `<BackupCodesCard>` rendered English literals,
  // and the in-session setup surface (`<MfaSettingsCard>`) is new: every sentence is catalogued here.
  setup_verify_intro: 'Enter the 6-digit code from your authenticator app to confirm setup.',
  setup_code_label: 'Verification Code',
  setup_backup_intro: 'Store these backup codes in a safe place. Each code can only be used once.',
  setup_backup_ack: 'I have saved these backup codes',
  setup_btn_complete: 'Complete Setup',
  backup_desc:
    'Backup codes let you into your account if you lose your authenticator device. Each code can only be used once.',
  backup_remaining: '{{count}} remaining',
  backup_regen_label: 'Enter a code from your authenticator app to regenerate your backup codes',
  backup_regen_success: 'Your backup codes were regenerated. Save the new codes now.',
  // D12c (B-1, B-2): the card saves the new codes and confirms a regenerate while codes remain.
  backup_btn_copy: 'Copy codes',
  backup_btn_download: 'Download codes',
  backup_btn_print: 'Print codes',
  backup_copied: 'Codes copied',
  backup_copy_failed: 'The codes could not be copied. Select them and copy them yourself.',
  backup_file_heading: 'Backup codes',
  backup_file_intro: 'Keep these codes in a safe place. Each code can only be used once.',
  backup_file_generated: 'Generated: {{date}}',
  backup_confirm_title: 'Regenerate your backup codes?',
  backup_confirm_body:
    'The backup codes you have left will stop working. Save the new codes as soon as they appear.',
  backup_confirm_btn: 'Regenerate codes',
  backup_confirm_cancel: 'Keep my codes',
  status_btn_disable: 'Turn Off MFA',
  enroll_intro_settings:
    'Turn on two-step verification for your {{appName}} account. Confirm your password to begin.',
  settings_disable_title: 'Turn off two-step verification',
  settings_disable_intro:
    'Enter a code from your authenticator app, or a backup code, to turn off two-step verification.',
  error_status_failed: 'Your two-step verification status could not be loaded. Try again.',
  error_setup_failed: 'Two-step verification could not be set up. Try again.',
  error_disable_failed: 'Two-step verification could not be turned off. Try again.',
  error_regen_failed: 'Your backup codes could not be regenerated. Try again.',

  // ───────────────────────────────────────────────────────────────────────────
  // `<PasskeyManagement>` — folded in from the prop-injected `i18n/passkeyManagement.*` bundle by
  // I18N-RENDER-002 (2026-08-20). It was delivered through `getPasskeyManagementI18n(language)` and
  // a `language` PROP that four apps had to remember to thread; it is now part of the `mfa`
  // namespace, so no host does anything.
  //
  // 🔴 The placeholders were converted from SINGLE to DOUBLE braces in the fold (`Q-BRACE-1`). A
  // namespace has one interpolator, and this corpus's is `{{…}}`. The old bundle's docblock justified
  // single braces with *"this package never touches i18next"* — which stopped being true the day
  // `mfa-ui` registered the `mfa` namespace, so the fold also retires a stale rationale.
  //
  // Flat keys, not a nested `passkey: {…}` object, and that is load-bearing: `resolveString` does a
  // FLAT `DEFAULT_MFA_STRINGS[key]` lookup, so nesting would make every one of these unresolvable on
  // the degrade path and would move `mfa-ui` into the NESTED roster that
  // `loginStringsWiring.contract.test.js` pins to exactly `['onboarding-ui', 'page-shell']`.
  passkey_title: 'Passkeys',
  passkey_registeredCount: '{{count}} registered',
  passkey_description:
    'Passkeys let you sign in with your fingerprint or face recognition — faster and more secure than passwords.',
  passkey_columnName: 'Name',
  passkey_columnCreated: 'Created',
  passkey_columnLastUsed: 'Last Used',
  passkey_columnActions: 'Actions',
  passkey_unnamedPasskey: 'Unnamed Passkey',
  passkey_removeAction: 'Remove',
  passkey_removeAriaLabel: 'Remove passkey {{name}}',
  passkey_never: 'Never',
  passkey_today: 'Today',
  passkey_yesterday: 'Yesterday',
  passkey_daysAgo: '{{count}} days ago',
  passkey_weeksAgo: '{{count}} weeks ago',
  passkey_deviceNameLabel: 'Device name (optional)',
  passkey_deviceNamePlaceholder: 'e.g. Work Laptop, Phone',
  passkey_registerAction: 'Register Passkey',
  passkey_registeringAction: 'Registering...',
  passkey_cancelAction: 'Cancel',
  passkey_removeDialogTitle: 'Remove Passkey',
  passkey_removeDialogBody:
    'Are you sure you want to remove "{{name}}"? You will no longer be able to sign in with this passkey.',
  passkey_removingAction: 'Removing...',
  passkey_emptyNotice:
    'No passkeys registered yet. Register a passkey to enable passwordless sign-in.',
  passkey_registerNewAction: 'Register New Passkey',
  passkey_retryAction: 'Retry',
  // ECOSYSTEM-EMAIL-001 Q-EE-21 unit 1: the passkey card's failures, worded here instead of in the hook (they were
  // English literals and, on a failed registration, the thrown message). The English words are unchanged.
  passkey_errorLoad: 'Unable to load passkeys. Please check your connection.',
  passkey_errorRegister: 'Failed to register passkey.',
  passkey_errorRegisterExpired: 'Passkey set-up timed out. Please start again.',
  passkey_errorCancelled: 'Passkey creation was cancelled',
  passkey_errorAlreadyRegistered: 'This device already has a passkey for your account.',
  passkey_errorRemove: 'Failed to remove passkey. Please try again.',
  passkey_errorRemoveGone: 'That passkey was already removed.',
  // Q-EE-34 P5 unit 4: a passkey is registered only after the person proves who they are again. The operation is
  // interpolated into the shared re-auth prompt ("Enter your credentials to {operationDescription}.").
  passkey_stepUpOperation: 'add a passkey',
  passkey_errorStepUpCancelled: "Confirm it's you to add a passkey.",
  // Q-EE-34 P5 unit 5: a passkey is a second factor. Removing one passes the same proof (Q-EE-45), and the server may
  // refuse it; a first passkey turns two-step verification on and answers backup codes once.
  passkey_stepUpRemoveOperation: 'remove a passkey',
  passkey_errorStepUpRemoveCancelled: "Confirm it's you to remove a passkey.",
  passkey_errorLastFactor:
    'This passkey is your only second factor. Turn off two-step verification first, or add another method.',
  passkey_errorEmailAlone:
    'Removing this passkey would leave an email code as your only second factor. Add another method first.',
  passkey_errorMethodsChanged: 'Your second-factor methods changed. Please try again.',
  passkey_backupCodesIntro:
    "This passkey turned on two-step verification. Save these backup codes: each one signs you in once if you can't use your passkey.",
  // VOLUME-PRICING-001 MFA-STEPUP-FLEET: the in-session step-up dialog (`createMfaStepUp`).
  stepUp_title: 'MFA Verification Required',
  stepUp_action_generic: 'This action',
  stepUp_requires_verification: '{{action}} requires additional verification',
  stepUp_close: 'Close modal',
  stepUp_cancel: 'Cancel',
  stepUp_error_no_token: 'The step-up answered without a session token',
  stepUp_error_failed: 'MFA verification failed',
  // N152 / N138: the step-up's failures are worded by status, never from the server's sentence or a thrown message.
  stepUp_error_session_ended: 'Your session has ended. Sign in again, then try again.',
  // Q-EE-34 P5 unit 3: the person closed the passkey prompt; the step-up stays open for another answer.
  stepUp_error_passkey_cancelled: 'The passkey prompt was closed. Try again, or enter a code.',
  // PERMISSIONS-CANON-001 S11 (b): the person holds NO second factor (`MFA_ENROLLMENT_REQUIRED`), so the dialog sends
  // them to set one up instead of asking for a code; and a method they never set up (`MFA_METHOD_NOT_ENROLLED`).
  stepUp_enrollment_title: 'Set up two-step verification',
  stepUp_enrollment_required:
    '{{action}} needs two-step verification, and you have not set it up yet. Set it up, then try again.',
  stepUp_enrollment_setup: 'Set up two-step verification',
  stepUp_error_method_not_enrolled:
    'That method is not set up for your account. Choose another, or set one up in your security settings.',
  // F-PASSKEY-HOST (2026-10-24): every open reads the person's methods first, and nothing is offered until it has.
  stepUp_methods_loading: 'Checking how you can verify…',
  // N152 / N138: chosen by HTTP status — 429 for the step-up and the enrolment screen; 0 (the API facade's request that
  // never completed) for the step-up.
  error_rate_limited: 'Too many attempts. Wait a few minutes, then try again.',
  error_network: 'We could not reach the server. Check your connection and try again.',
  // The sign-in enrolment's ten-minute token ran out (401).
  error_enrollment_expired: 'Your setup time ran out. Return to sign-in and start again.',
  // N139 — `useMfaRefusalText`: a SIGNED-IN person's wrong password or code (every API's 403
  // `CREDENTIAL_INCORRECT`). The password sentence is auth-shell's `authShell.credential.incorrect.password`,
  // word for word (`credentialRecheckRefusal.contract` holds the two equal).
  error_password_incorrect: 'That password is incorrect. Try again.',
  error_code_incorrect: 'That code is incorrect. Check your authenticator app and try again.',
  // ECOSYSTEM-EMAIL-001 Q-EE-22, owner ruling 2026-10-11 "Plain, retry" (the words verbatim): a second-factor change
  // (turning it on or off, new backup codes, a step-up) is refused 503 `AUDIT_RECORD_UNAVAILABLE` when its audit record
  // could be neither written nor kept for delivery — nothing changed, and trying again is safe.
  error_change_not_recorded:
    'Your change was not made because it could not be recorded. Please try again in a moment.',
  // Q-EE-41: a passkey ceremony is refused 503 `PASSKEY_CHALLENGE_STORE_UNAVAILABLE` when its challenge store cannot
  // answer — nothing was checked, and trying again is safe.
  error_passkey_unavailable: 'Passkeys are unavailable for a moment. Please try again.',
  // ErrorState Unit B — `useMfaRefusalText`: the set-up and backup-code routes' own refusals, by their codes
  // (409 `MFA_ALREADY_ENABLED`, 400 `MFA_NO_PENDING_SECRET`, 400 `MFA_NOT_ENABLED`).
  error_already_enabled: 'Two-step verification is already set up for this account.',
  error_setup_expired:
    'This setup is no longer pending. Start setting up two-step verification again.',
  error_not_enabled: 'Two-step verification is not set up for this account.',
  // Owner ruling 2026-10-06 "Keep refusal, explain it": a code to a number that texted STOP stays refused, and the
  // person is told where to text START (the number PortPuffin sends codes from).
  error_sms_opted_out:
    'Your number has opted out of texts from {{number}}. Text START to {{number}}, then send the code again.',
  error_sms_opted_out_no_number:
    'Your number has opted out of our texts. Text START to the number our codes come from, then send the code again.',
  error_sms_opted_out_by_company:
    "Your number is on this company's opt-out list for texts, so we can't text you a code. Ask the company to restore texts, or choose email instead.",
  // D1 errorCode #2b: a send inside the server's pace (429 `RATE_LIMIT_EXCEEDED`, `retryAfter` seconds).
  error_send_too_soon: 'Please wait {{seconds}} seconds before asking for another code.',
  error_send_failed: 'The code could not be sent. Check your connection and try again.',
  // A method the person never set up (400 `MFA_METHOD_NOT_ENROLLED`): retrying can never succeed. True in every
  // `MfaVerification` host (sign-in, device registration, add-PIN, re-identification, unregistration).
  error_method_not_enrolled:
    "That verification method isn't set up for your account. Choose another method, or ask an administrator to set it up.",
  // D1 errorCode #2: a sign-in's send-otp refusals that end the challenge (`MFA_CHALLENGE_INVALID` /
  // `MFA_SESSION_EXPIRED`), in login-shell's own words (`login.error.mfaChallengeInvalid` / `.mfaSessionExpired`).
  error_challenge_invalid: 'Your verification session is no longer valid. Please sign in again.',
  error_challenge_expired: 'Your verification session expired. Please sign in again.',
  // LIFE-SHORTLISTED-001 LOGIN-CONVERGENCE (coordinator ruling (a), 2026-10-24): a sign-in whose enrolled second factors
  // this app cannot serve is refused `MFA_NO_USABLE_METHOD` before any challenge, and a challenge offering nothing (an
  // older server) says the same. ONE sentence: login-shell's refusal for that code renders this leaf.
  error_no_usable_method:
    'None of the second factors you have set up can be used to sign in here. Sign in where you set it up, or ask your administrator to reset your second factor.',
  // LIFE-SHORTLISTED-001 Q-LS-74: a code text whose outcome is not known (its carrier answer was lost; never re-sent).
  notice_sms_in_doubt:
    'Your code may already be on its way. Check your messages before you ask for another one.',
  // MFA-STEPUP-FLEET U-B: the registry's labels (`@puffin-core/mfa-actions`, `labelKey`) — the ONE
  // place an MFA-gated action and its criterion class are named for people; the step-up dialog and
  // the tenant MFA-policy editor both read them. `mfaGatedActionsRegistry` proves every registered
  // action has its leaf in every language.
  action_refund: 'Issuing a refund',
  action_price_override: 'Overriding a line price',
  action_discount_apply: 'Applying a manual discount',
  action_void_transaction: 'Voiding a transaction',
  action_cash_drawer_open: 'Opening the cash drawer outside a sale',
  action_delete_customer: 'Deleting a customer',
  action_export_data: 'Exporting data',
  action_reveal_mail_name_id_numbers: 'Revealing a mail name’s full ID number',
  action_modify_inventory: 'Approving an inventory adjustment',
  action_system_settings: 'Changing system settings',
  action_order_payment_update: "Updating an order's payment or issuing a refund",
  action_order_cancel: 'Cancelling an order',
  action_platform_order_refund: "Refunding a company's order",
  action_platform_order_cancel: "Cancelling a company's order",
  action_platform_tenant_owner_restore: "Restoring a company's locked-out Owner",
  action_platform_passkey_register: 'Registering a passkey',
  action_platform_passkey_remove: 'Removing a passkey',
  action_platform_stir_shaken_cert_rotate: "Rotating a company's STIR/SHAKEN certificate",
  action_platform_port_in_cancel: "Cancelling a company's port-in",
  action_platform_connect_account_unlink: "Unlinking a company's Stripe account",
  action_price_list_edit: "Editing a product's price list",
  action_coupon_edit: 'Creating, changing or deleting a coupon',
  action_partner_pricing_tier_set: "Changing a partner's pricing tier",
  action_partner_api_key_edit: 'Issuing or revoking a partner API key',
  action_partner_delete: 'Deleting a partner',
  action_partner_status_change: 'Approving, rejecting, suspending or reactivating a partner',
  action_stock_count_set: "Setting a product's stock count",
  action_mail_address_change:
    "Adding an email account or a forwarder, or changing an email account's password",
  action_payroll_submit: 'Submitting a payroll run',
  action_payroll_cancel: 'Cancelling a payroll run',
  action_payroll_reset: 'Resetting a payroll run',
  action_payroll_skip: 'Skipping a pay period',
  action_payroll_cancel_submission: 'Cancelling a submitted payroll',
  action_payroll_approve: 'Approving a payroll run',
  action_payroll_adjustment_edit: 'Creating or voiding a payroll adjustment',
  action_contractor_payment_create: 'Paying a contractor',
  action_garnishment_edit: 'Creating, changing or releasing a garnishment',
  action_gl_export_edit: 'Creating or voiding a general-ledger export',
  action_payroll_connection_edit: 'Connecting, refreshing or disconnecting the payroll provider',
  action_password_change: 'Changing your password',
  action_mfa_disable: 'Turning off two-factor authentication',
  action_app_charge_accept: 'Accepting a recurring app charge',
  action_mfa_policy_update: 'Changing the two-step verification policy',
  action_field_permissions_edit: "Changing a role's field permissions",
  action_approval_chain_edit: 'Changing the approval chain or delegating an approval',
  action_settings_edit: 'Changing payroll settings',
  action_tax_setup_edit: 'Changing the tax setup',
  action_company_form_sign: "Signing payroll forms on the signatory's behalf",
  action_payday_bank_account_link: 'Linking, adding or verifying the payroll funding bank account',
  action_schedule_publish: 'Publishing a schedule',
  action_approval_decide: 'Deciding an approval request',
  action_approval_delegate: 'Delegating an approval or revoking a delegation',
  action_location_access_edit: 'Changing who may work at which locations',
  action_compliance_jurisdiction_attest: 'Attesting the compliance jurisdictions',
  action_overtime_rules_edit: 'Changing the overtime rules',
  action_planning_settings_edit: 'Changing planning settings',
  action_ewa_withdraw: 'Requesting an earned-wage withdrawal',
  action_emergency_broadcast_send: 'Sending an emergency broadcast',
  action_engagement_settings_edit: 'Changing engagement settings',
  action_sentiment_collection_toggle: 'Turning sentiment collection on or off',
  action_marketplace_purchase: 'Purchasing marketplace content',
  action_marketplace_publish: 'Publishing or unpublishing marketplace content',
  action_course_publish: 'Publishing a course',
  action_short_publish: 'Publishing a training short',
  action_generated_content_publish: 'Publishing generated content',
  action_certification_award: 'Awarding, approving or renewing a certification',
  action_certification_revoke: 'Revoking a certification',
  action_content_delete:
    'Deleting a course, program, learning path, assessment or certification definition',
  action_tenant_settings_edit: 'Changing training settings',
  action_connector_credentials_set: "Setting a connector's credentials",
  action_contact_delete: 'Deleting a contact',
  action_site_delete: 'Deleting a site',
  action_outreach_settings_edit: 'Changing outreach settings',
  action_recording_export: 'Downloading or forwarding a call recording',
  action_recording_delete: 'Deleting a call recording',
  action_voicemail_delete: 'Deleting a voicemail',
  action_fax_delete: 'Deleting a fax',
  action_legal_hold_edit: 'Placing or lifting a legal hold',
  action_retention_force_run: 'Running a retention purge now',
  action_ivr_publish: 'Publishing an IVR tree',
  action_number_porting:
    'Starting, resubmitting, cancelling, approving or rejecting a number port, or managing its PIN or temporary forwarding',
  action_number_purchase: 'Purchasing a phone number',
  action_number_release: 'Releasing a phone number',
  action_number_departure_confirm: 'Confirming that a phone number left its carrier account',
  action_stir_shaken_cert_rotate: 'Rotating a STIR/SHAKEN certificate',
  action_stripe_connect_edit:
    'Creating the company’s Stripe Connect account or its onboarding link',
  action_dnc_delete: 'Deleting a do-not-call entry',
  action_dnc_export: 'Exporting the do-not-call list or answering a GDPR request',
  action_compliance_evidence_export: 'Exporting compliance evidence',
  action_mailbox_holder_delete: 'Deleting a mailbox-holder subscription',
  action_mailbox_holder_pin_reset: "Resetting a mailbox holder's PIN",
  action_mailbox_holder_billing_edit: "Changing or cancelling a mailbox holder's billing",
  action_cloned_voice_delete: 'Deleting a cloned voice',
  action_ai_skill_test_invoke: 'Running an AI skill on live data as a test',
  action_api_key_edit: 'Creating or revoking an API key',
  action_webhook_edit: 'Creating, changing or deleting a webhook',
  action_alert_destination_edit: 'Creating, changing or deleting an alert destination',
  action_scim_token_edit: 'Creating or revoking a SCIM token',
  action_carrier_credentials_set: "Setting a carrier account's credentials",
  action_carrier_account_delete: 'Deleting a carrier account',
  action_sip_device_edit: 'Registering, changing, re-provisioning or deregistering a SIP device',
  action_sip_firmware_stage: 'Staging new firmware on a SIP device',
  action_sso_connection_edit: 'Creating, changing or deleting an SSO connection',
  action_outbound_campaign_launch: 'Launching, changing or resuming an outbound campaign',
  action_outbound_campaign_delete: 'Deleting an outbound campaign or template',
  action_outbound_template_approve: 'Approving an outbound template',
  action_emergency_broadcast_retry: 'Re-sending an emergency broadcast',
  action_retention_settings_edit: 'Changing call-recording retention',
  action_consent_settings_edit: 'Creating, changing or deleting a consent capture form',
  action_emergency_keywords_edit: 'Changing the emergency keywords',
  action_claim_submit: 'Submitting a carrier claim',
  action_claim_status_set: "Setting a claim's outcome",
  action_claim_edit: "Changing a claim's amounts",
  action_crawler_agent_key_edit: 'Minting or revoking a crawler agent key',
  action_claim_delete: 'Deleting a claim or claim line',
  action_invoice_delete: 'Deleting an invoice or invoice line',
  action_quote_delete: 'Deleting a quote',
  action_discrepancy_delete: 'Deleting a discrepancy',
  actionClass_money: 'Money',
  actionClass_credentials: 'Credentials and secrets',
  actionClass_destruction: 'Destruction and export',
  actionClass_publication: 'Publication',
  actionClass_authorization: 'Authorization',
  actionClass_compliance_settings: 'Compliance settings',
  // PERMISSIONS-CANON-001 S11 (a) / E3: <MfaPolicyBody>, the company policy every app mounts (`mfa-ui/policy`).
  policy_title: 'Two-step verification policy',
  policy_intro:
    'Choose which actions ask for two-step verification. Exports, credentials and money actions ask by default: untick one to turn it off for your company. Every change is recorded.',
  policy_read_only: 'You can view this policy, but your role cannot change it.',
  policy_loading: 'Loading the two-step verification policy…',
  policy_error_load: 'The two-step verification policy could not be loaded.',
  policy_error_load_forbidden: 'Your role cannot view the two-step verification policy.',
  policy_retry: 'Try again',
  policy_action_default_on: '(on by default)',
  policy_action_required: '(always required)',
  policy_grace_label: 'Grace period (days)',
  policy_grace_hint:
    'Days after the start date before two-step verification is required, from 0 to 365.',
  policy_enforce_after_label: 'Requirement starts (UTC)',
  policy_enforce_after_hint: 'The date, in UTC, from which two-step verification is required.',
  policy_save: 'Save policy',
  policy_saving: 'Saving…',
  policy_saved: 'The policy was saved.',
  policy_error_save: 'The policy could not be saved. Try again.',
  policy_error_save_forbidden:
    'Your role cannot change the two-step verification policy. Ask someone who can manage it.',
  policy_error_save_invalid:
    'The policy was not accepted. Check the actions and the dates, then save again.',
  // SECREQ S7 (a): the policy's write asks the app's step-up (`mfa_policy_update`); the prompt names the change.
  policy_stepUpOperation: 'change the two-step verification policy',
  policy_error_save_step_up_cancelled:
    "Confirm it's you to change the two-step verification policy.",
  policy_error_grace_range: 'Enter a grace period from 0 to 365 days.',
  // PERMISSIONS-CANON-001 S11 E1: <RoleMfaRequirementField>, one role in the company policy, in a role editor.
  policy_role_required_label: 'Requires two-step verification',
  policy_role_required_hint:
    'People in this role must set up two-step verification. The change is saved to the company policy and recorded.',
  // SECREQ S7 (a): the same box for the policy's other two role lists (owner ruling "Enforce, like MFA").
  policy_role_passkey_required_label: 'Requires a passkey',
  policy_role_passkey_required_hint:
    'People in this role must set up a passkey. The change is saved to the company policy and recorded.',
  policy_role_pin_required_label: 'Requires a PIN',
  policy_role_pin_required_hint:
    'People in this role must set up a PIN to sign in at a register. The change is saved to the company policy and recorded.',
});

/**
 * String interpolation for this corpus's `{{param}}` placeholders.
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20): bound from the ONE shared implementation rather than
 * hand-rolled here. Eight copies of this function existed across the ecosystem and they were NOT
 * identical — three crashed on a nullish template, one expanded a `$&` in a value as a regex
 * replacement pattern, and one read prototype keys.
 *
 * ⚠️ Both parameters are load-bearing and are asserted by `tests/unit/strings.test.js`:
 *
 *   - `braces: 'double'` — this corpus uses `{{maskedPhone}}`, where `@puffin-core/re-auth` uses
 *     `{operationDescription}`. The two are deliberately different and may not be unified without
 *     changing both bundles.
 *   - `onMissing: 'empty'` — an unsupplied placeholder is BLANKED here (`'{{nope}} gone'` → `' gone'`),
 *     where re-auth echoes the token back.
 *
 * The import is the React-free `/strings-kit` subpath, never the package barrel: this file is part of
 * the leaf-only `strings/` subtree that `@puffin-core/i18n-namespaces` loads at app boot, so a barrel
 * import would drag `createAppI18n` into ten apps' boot chunks.
 *
 * @param {string} template - String with `{{key}}` placeholders
 * @param {Object} [params] - Key-value pairs for substitution
 * @returns {string} Interpolated string
 */
export const interpolate = createInterpolator({ braces: 'double', onMissing: 'empty' });
